/**
 * Marketing, Creative & Digital — 17 documents.
 *
 * Everything in this category is really one question asked seventeen ways: after the invoice is
 * paid, who owns the thing that was made, and where is the brand allowed to run it?
 *
 * Three distinctions run through every entry and founders collapse all three:
 *   ownership   — the asset is yours outright, by written assignment
 *   licence     — they own it, you may use it, in named channels, for a named period
 *   access      — the account, the pixel, the audience and the history the work lives inside
 * A brand can own the copyright in a video and still be unable to run it as an ad, and can have a
 * perfect licence to an image while an agency holds the ad account it would run in.
 */
export const MARKETING_DOCS = [
  {
    id: "marketing-agency-agreement",
    name: "Marketing Agency Agreement",
    category: "marketing",
    subcategory: "Agencies",
    aliases: [
      "Agency of Record Agreement",
      "Marketing Services Agreement",
      "Growth Agency Agreement",
      "Retainer Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Marketing Agency",
    whenYouNeedIt:
      "Before an agency starts work, and specifically before you give anyone administrative access to your ad accounts, analytics, email platform or store. Access is granted in a two-minute settings change and is very hard to claw back afterwards.",
    whatItDoes:
      "Sets the scope of services, how the agency is paid, how media budget is handled, who owns the creative they produce, and who holds the accounts, data and audiences the work is built on when the relationship ends.",
    whyItMatters:
      "Two things go wrong, and they are both about ownership rather than performance. First, paying an invoice is not an assignment: without written transfer language the agency owns the concepts, the layouts and the edits they produced, and you have an implied permission at best. Second, and more common, the agency runs everything inside their own business manager — your ad account, your pixel, your custom audiences, your creative library and eighteen months of learning. When you leave, you can rebuild the creative. You cannot rebuild the account history, and starting a new pixel from zero is a real, measurable cost paid in wasted media.",
    majorTerms: [
      "Scope of services and named deliverables",
      "Fee structure: retainer, project fee, percentage of media spend or hybrid",
      "Who is invoiced for media and who carries the payment liability",
      "Markups on pass-through production and media costs",
      "Ownership and assignment of creative and working files",
      "Ownership of platform accounts, pixels, audiences and data",
      "Reporting cadence and the metrics reported",
      "Approval process and turnaround times",
      "Term, notice period and termination for convenience",
      "Category exclusivity and conflict policy",
      "Subcontractors and offshore production",
      "Transition and offboarding obligations",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of deliverables",
        why: "Silence, or a licence in place of an assignment, means the agency keeps the winning creative and you pay again to keep using it after you part ways.",
      },
      {
        clause: "Account and platform ownership",
        why: "If the ad account, pixel and audiences sit under the agency's business manager, leaving costs you the historical data that makes the account cheap to run.",
      },
      {
        clause: "Percentage-of-spend fees",
        why: "A fee that rises with budget rewards spending more rather than spending well, and it needs a floor, a ceiling or a performance element to be a real incentive.",
      },
      {
        clause: "Pass-through costs and markups",
        why: "Undisclosed markups on production, influencer fees and stock licences quietly move a chunk of your budget into the agency's margin.",
      },
      {
        clause: "Termination notice and transition",
        why: "A ninety-day notice period with no obligation to hand back assets and access means three months of paying for a relationship you have already left.",
      },
      {
        clause: "Third-party licensed assets inside the work",
        why: "Stock imagery, music and fonts come with their own terms, and an assignment of the agency's work does not extend the licence underneath it.",
      },
      {
        clause: "Category conflicts",
        why: "An agency running two brands in the same set can end up bidding your accounts against each other and sharing what works between them.",
      },
    ],
    cpgIssues: [
      "Retail media accounts — marketplace advertising, retailer portals and grocery delivery platforms — are usually opened by whoever does the work, and a brand that lets an agency open them under agency credentials loses the account along with the agency.",
      "Co-op and market development funds committed by a retailer or distributor often have documentation and proof-of-performance requirements, so the agency needs to produce evidence in the format the account will accept, not just a slide.",
      "Claims about a functional beverage or supplement made in an ad are the brand's exposure, not the agency's, so the agreement should require the agency to route claim language through your review and to hold a documented approval before anything goes live.",
      "Agencies get access to landed cost, promotional calendars and retailer-specific margin in order to model a promotion, and that material is confidential to your retail partners as well as to you.",
      "Trade and shopper marketing assets — shelf talkers, case cards, sell sheets, booth graphics — are produced to a printer's or a retailer's specification, so the deliverable is a print-ready file to spec, not a web export.",
      "Seasonal and shelf-reset calendars drive CPG timing, and an agency working to a generic campaign calendar will miss the windows that actually decide distribution.",
    ],
    founderMistakes: [
      "Letting the agency create the ad account inside their own business manager, then losing eighteen months of pixel data and audience history in the switch to a new team.",
      "Paying six figures over a year and discovering there was no assignment clause, so the agency's licence to the creative ended with the retainer.",
      "Agreeing a percentage-of-spend fee with no performance element, then being talked into scaling budget on a campaign that was not working.",
      "Approving pass-through invoices without a markup disclosure and finding the influencer fees on the invoice were well above what the creators were actually paid.",
      "Never asking who else in the category the agency worked with, and briefing a competitor's team on the launch calendar.",
    ],
    negotiationPoints: [
      "Require that every platform account is created under the brand's own business manager and ownership, with the agency added as a user rather than the owner.",
      "Ask for a present assignment of all deliverables plus delivery of layered and editable working files as a condition of final payment on each project.",
      "Set an explicit offboarding obligation: admin transfer, asset delivery, audience export where the platform allows it, and removal of agency access within a stated number of days.",
      "Require written disclosure of any markup on pass-through costs, and the right to see third-party invoices.",
      "Negotiate a shorter notice period, or a right to stop work immediately with fees running only to the notice date.",
      "Ask for a named category conflict list and a commitment to notify you before taking on a directly competing brand.",
      "Tie a portion of the fee to agreed outcomes rather than to spend.",
    ],
    redFlags: [
      "Ad accounts, pixels or email platforms held under the agency's ownership with no transfer obligation.",
      "No assignment language anywhere in the agreement, or a licence limited to the term.",
      "Automatic renewal for a further year unless notice is given months in advance.",
      "Refusal to disclose markups or to show third-party invoices.",
      "Working files described as agency property and excluded from the deliverables.",
      "No stated obligation to hand over anything on termination.",
    ],
    beforeYouSign: [
      "A written inventory of every platform account, who created it, who owns it and who has admin.",
      "A deliverables list that names editable working files, not just final exports.",
      "The approval workflow for anything containing a product claim, agreed in writing before the first campaign.",
      "Your own model of the fee against realistic spend, including the percentage-of-spend mechanics at scale.",
    ],
    related: [
      "advertising-agency-agreement",
      "branding-agency-agreement",
      "social-media-management-agreement",
      "ip-assignment-agreement",
      "work-for-hire-agreement",
      "influencer-agreement",
    ],
    attorneyReview: "yes",
    templateId: "marketing-agency-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "branding-agency-agreement",
    name: "Branding Agency Agreement",
    category: "marketing",
    subcategory: "Agencies",
    aliases: [
      "Brand Identity Agreement",
      "Naming and Identity Agreement",
      "Brand Development Agreement",
      "Rebrand Agreement",
    ],
    stages: ["idea", "pre-launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Branding or Identity Agency",
    whenYouNeedIt:
      "Before commissioning a name, a logo, a brand architecture or a full rebrand — the work that produces the assets a future buyer will diligence hardest.",
    whatItDoes:
      "Commissions brand identity work — naming, mark, typography, colour system, brand book, packaging direction — and, if written properly, transfers ownership of everything produced to the company.",
    whyItMatters:
      "The name and the mark are the two assets a CPG business is ultimately sold on, and both routinely sit with the wrong owner. Without a written assignment the agency owns the logo it drew and the identity system it built; you paid for the project, not the rights. Two further traps are specific to identity work. An agency that presents a name has usually checked a domain and a handle, not much more, and clearance is a separate exercise you should run before anything is printed. And a brand book delivered as a PDF is a description of an asset, not the asset — the layered files, the packaged fonts and the colour build are what a printer, a co-packer and the next designer actually need.",
    majorTerms: [
      "Scope: naming, mark, identity system, brand book, packaging direction",
      "Number of concepts and revision rounds",
      "Present assignment of the name, mark and all identity assets",
      "Delivery of layered source files, colour builds and packaged fonts",
      "Font, stock and illustration licences and who holds them",
      "Responsibility for trademark searching and clearance",
      "Rights to unselected concepts and routes",
      "Portfolio, case study and awards rights, and when they begin",
      "Payment schedule tied to delivery of files, not to presentations",
      "Term, kill fee and what is owed on early termination",
    ],
    clausesToWatch: [
      {
        clause: "Assignment versus licence of the identity",
        why: "A licence to use your own logo is a permission that can expire or be revoked, and it will not survive a buyer's diligence.",
      },
      {
        clause: "Font licensing",
        why: "A desktop font licence covers a designer's laptop, not mass reproduction on packaging or embedding in a website, and the shortfall is discovered at the printer.",
      },
      {
        clause: "Trademark clearance responsibility",
        why: "If the agreement is silent, a presented name has been checked for a domain and nothing else, and the cost of finding out late is a full artwork and print cycle.",
      },
      {
        clause: "Delivery of working files",
        why: "Without the layered identity files and the colour builds, every future SKU, size and co-brand starts by paying someone to rebuild what you already own.",
      },
      {
        clause: "Portfolio and publication rights",
        why: "An agency free to publish immediately can show your unreleased packaging and your positioning before the brand is on shelf.",
      },
      {
        clause: "Rights in unselected routes",
        why: "The direction you did not pick can turn up on a competitor a year later unless the agreement says who owns the rejected work.",
      },
    ],
    cpgIssues: [
      "A CPG identity has to work as a packaging system, not just a logo: the mark needs to survive a can's curve, a shrink sleeve's distortion, a small-format pouch and a shelf viewed from six feet away, and none of that is testable from a screen presentation.",
      "Colour has to be specified for print, not for screen — a printer needs a build or a matched colour reference for each substrate, and a brand book with only hex values is unusable at press.",
      "Fonts appearing on packaging need reproduction licences that cover mass printing, and the licence usually has to be held by the brand rather than by the agency that bought a seat.",
      "Flavour and variant naming becomes a system, not a one-off — the identity should specify how the fourth flavour and the second format are named and coloured, because you will be adding them long after the agency has gone.",
      "Any descriptive or functional element of a name interacts with what you can put on a label, so the naming work and the label review need to happen in the same conversation rather than months apart.",
      "Rebrands strand printed inventory: cans, labels, cartons and shippers already produced to the old identity carry minimum order quantities that do not disappear because the logo changed.",
    ],
    founderMistakes: [
      "Falling in love with a name presented in a deck, printing labels, and only then running a proper clearance search.",
      "Receiving the brand book as a PDF and nothing else, then paying a second designer to rebuild the logo as a vector file for the can printer.",
      "Using a font on packaging under the agency's desktop licence and being stopped by the printer at the press check.",
      "Never getting an assignment for the logo, then having to chase a dissolved agency for a signature during a financing.",
      "Approving an identity that looked beautiful in a flat mockup and failed completely on a shrink sleeve.",
    ],
    negotiationPoints: [
      "Ask for a present assignment of the name, the mark, the identity system and all supporting artwork, with a further-assurances obligation.",
      "Require delivery of layered source files, vector marks, colour builds for print and a written list of every third-party asset used.",
      "Have fonts licensed in the company's name, with the licence documents delivered as part of the handover.",
      "Agree in writing who runs trademark clearance and at what point, and do not commission packaging before it is done.",
      "Delay portfolio and case study rights until after public launch, and exclude anything showing unreleased packaging.",
      "Ask for physical proofing on the actual substrate before final approval, not just flat mockups.",
    ],
    redFlags: [
      "A licence to use the identity rather than an assignment of it.",
      "No delivery of vector or layered files in the deliverables list.",
      "An agency that will not say what font, stock or illustration licences the work depends on.",
      "Immediate portfolio rights covering unreleased packaging.",
      "Naming work presented with no clearance step described anywhere in the process.",
    ],
    beforeYouSign: [
      "A written deliverables list naming file formats, vector marks and colour builds for print.",
      "Agreement on who commissions and pays for trademark clearance, and when.",
      "The packaging formats the identity has to work on, listed before concepts begin.",
    ],
    related: [
      "packaging-design-agreement",
      "graphic-design-agreement",
      "trademark-assignment",
      "ip-assignment-agreement",
      "design-ownership-agreement",
      "marketing-agency-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "packaging-design-agreement",
    name: "Packaging Design Agreement",
    category: "marketing",
    subcategory: "Design and web",
    aliases: [
      "Label Design Agreement",
      "Package Artwork Agreement",
      "Structural and Graphic Packaging Design",
      "Artwork Development Agreement",
    ],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Packaging Designer or Design Studio",
    whenYouNeedIt:
      "Before any artwork work starts on a label, can, carton, pouch or bottle — and again before each new flavour, size or format, unless the original agreement already covers extensions.",
    whatItDoes:
      "Commissions the graphic artwork applied to a package, defines what gets delivered and in what form, and transfers ownership of the artwork and the working files to the brand.",
    whyItMatters:
      "This is where CPG differs from every other design brief, and the difference is the file. A flat JPEG or a print-ready PDF is a snapshot of one SKU at one moment. The dieline and the layered working file are the packaging system: the next flavour, the next size, the co-packer who needs the panel moved, the reformulation that changes the ingredient statement and the nutrition panel all require the editable file. A designer who delivers only flattened artwork holds the brand's entire packaging system, and every future change — including changes you are obliged to make — runs back through them at their price and their timeline. Paying the invoice does not move the file, and it does not move ownership either.",
    majorTerms: [
      "SKUs, panels and formats in scope",
      "Rounds of revision and what counts as a new round",
      "Who supplies the dieline and who owns it",
      "Deliverables: print-ready files, layered native files, packaged fonts and links",
      "Present assignment of the artwork and all working files",
      "Font, stock, illustration and photography licences inside the artwork",
      "Pricing for flavour, size and format extensions",
      "Printer liaison, pre-press corrections and press check attendance",
      "Timeline against the print and production window",
      "Responsibility for panel content accuracy and who signs off",
      "Payment schedule tied to file delivery",
    ],
    clausesToWatch: [
      {
        clause: "Definition of deliverables",
        why: "If the deliverable is 'final artwork', a designer can satisfy it with a flattened print PDF and keep the layered file that makes every future change possible.",
      },
      {
        clause: "Dieline ownership and supply",
        why: "The dieline is specific to a supplier's tooling and pack format, and without it in your hands a change of can supplier or carton converter restarts the artwork from nothing.",
      },
      {
        clause: "Extension rights and pricing",
        why: "Unpriced extensions mean the fourth flavour is quoted at whatever the designer likes, at the moment you have a booked line time and no alternative.",
      },
      {
        clause: "Font and stock licences",
        why: "Packaging is mass reproduction, and desktop or standard stock licences frequently do not cover it — the shortfall surfaces at pre-press, mid print window.",
      },
      {
        clause: "Pre-press corrections",
        why: "Printers routinely send back corrections, and if fixing them is out of scope you are negotiating a change order against a booked press date.",
      },
      {
        clause: "Panel content responsibility",
        why: "Designers set type; they do not verify the ingredient statement, the nutrition panel or the net content, and the agreement should say plainly that the brand signs off on panel content.",
      },
      {
        clause: "Payment tied to presentation rather than delivery",
        why: "Paying in full at final approval, before native files arrive, removes the only leverage you have to get them.",
      },
    ],
    cpgIssues: [
      "The dieline comes from the can maker, carton converter or label printer and is specific to their tooling, so a design built on one supplier's dieline is not portable to another without rework.",
      "Reformulations, supplier changes and net-content adjustments all change the panel, and each one requires the layered file — this is the most common reason a brand ends up paying to rebuild artwork it already owns.",
      "Shrink sleeves distort artwork around a bottle's contour and require a distortion-compensated file; a flat design approved on screen can be unusable on the actual pack.",
      "Barcode placement, quiet zones and scan testing are part of the artwork, and a barcode that fails at a retailer's scanner stops a delivery at the door.",
      "Colour must be built for the substrate — a matched colour on a white label, a metallic can and a kraft carton are three different builds, and a single brand colour spec will not carry across them.",
      "Label and can minimum order quantities mean an artwork error is not a reprint of a few sheets; it is a full minimum run written off, plus the lead time to make another.",
      "Certification and claim marks on pack — organic, kosher, non-GMO, gluten-free and similar — have their own placement and usage rules from the certifier, and the designer needs those rules rather than a screenshot of the logo.",
    ],
    founderMistakes: [
      "Accepting flattened print PDFs as the only deliverable, then paying a second studio to recreate the whole label to change a single line in the ingredient statement.",
      "Losing the working files when a designer went quiet during a fee dispute, with a co-packer line time booked and no artwork to send the printer.",
      "Discovering at pre-press that the display font was licensed for desktop use only, days before the press date.",
      "Never obtaining the dieline in editable form, so moving to a second can supplier meant re-originating the artwork.",
      "Assuming the designer had checked the nutrition panel because it was in the file they laid out.",
      "Commissioning a single SKU with no extension pricing, then being quoted per-SKU rates for a four-flavour line months later.",
    ],
    negotiationPoints: [
      "Define deliverables explicitly: print-ready files, layered native files, all linked assets, packaged fonts and the dieline, listed by name.",
      "Hold a meaningful final payment until the native file package is delivered and opens cleanly on your side.",
      "Agree extension pricing for additional flavours, sizes and formats at the outset, while you still have leverage.",
      "Require a written list of every third-party font, stock image and illustration in the artwork, with the licence terms and who holds them.",
      "Ask for a present assignment covering the artwork, the layouts and any brand elements created for the project.",
      "Include pre-press corrections and one press check in the base scope rather than treating them as change orders.",
      "Have the designer work from the actual supplier dieline before concepts are approved, not after.",
    ],
    redFlags: [
      "Working or native files described as the designer's property, or excluded from the deliverables.",
      "No mention of the dieline anywhere in the agreement.",
      "A licence to use the artwork rather than an assignment of it.",
      "Full payment due on approval of a flat visual, with files delivered later.",
      "No extension pricing and no obligation to quote extensions on agreed terms.",
      "Refusal to disclose the font and stock licences the artwork depends on.",
    ],
    beforeYouSign: [
      "The supplier dieline in hand, or a written commitment about who obtains it and when.",
      "A named deliverables list including layered native files, packaged fonts and links.",
      "The print and production calendar, so the design timeline is set against a real press date.",
      "A decision on who is accountable for panel content accuracy, in writing.",
    ],
    related: [
      "packaging-artwork-ownership",
      "design-ownership-agreement",
      "graphic-design-agreement",
      "label-supply-agreement",
      "packaging-specification-agreement",
      "ip-assignment-agreement",
    ],
    attorneyReview: "yes",
    templateId: "packaging-design-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "graphic-design-agreement",
    name: "Graphic Design Agreement",
    category: "marketing",
    subcategory: "Design and web",
    aliases: [
      "Freelance Design Agreement",
      "Design Services Agreement",
      "Creative Services Agreement",
      "Studio Agreement",
    ],
    stages: ["pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Freelance Designer or Studio",
    whenYouNeedIt:
      "Whenever a freelancer or studio produces marketing collateral: sell sheets, trade show graphics, shelf talkers, social templates, email design, decks, banner sets and point-of-sale material.",
    whatItDoes:
      "Engages a designer for defined collateral, sets revision limits and timelines, and transfers ownership of the finished work and the editable files to the brand.",
    whyItMatters:
      "Marketing collateral looks low-stakes next to packaging, and that is exactly why the paperwork gets skipped. The consequence shows up as friction rather than as a single disaster: the sell sheet that has to be updated the week a distributor asks for it, the social template that only the original designer can edit, the trade show banner that cannot be resized for a different booth. Multiply that by every asset and by every designer you have used, and the brand's marketing library is a pile of exports nobody can change. The fix is the same one that fixes packaging — an assignment plus the editable files, agreed before the work starts.",
    majorTerms: [
      "Defined list of assets and their formats",
      "Revision rounds included and hourly rate beyond them",
      "Present assignment of the deliverables",
      "Delivery of editable working files and template files",
      "Third-party font, stock and illustration licences",
      "Turnaround times and rush rates",
      "Portfolio and credit rights",
      "Payment schedule and kill fee",
      "Subcontractor flow-down for retouchers and illustrators",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of templates versus finished assets",
        why: "A designer who assigns the exported artwork but keeps the template file makes every future variation a paid job.",
      },
      {
        clause: "Stock imagery inside the deliverable",
        why: "A standard stock licence often covers digital use but not print runs or packaging, so an asset that works as a banner cannot become a case card.",
      },
      {
        clause: "Revision definition",
        why: "If a 'round' is undefined, a set of small comments can be billed as three rounds and the project runs past the trade show it was made for.",
      },
      {
        clause: "Subcontractors",
        why: "Freelancers pass work to retouchers and illustrators, and those people own what they made unless the designer is required to bind and assign onward.",
      },
      {
        clause: "Portfolio rights before launch",
        why: "Collateral often reveals pricing, a retailer launch or a new SKU, and an immediate portfolio right puts that in public before you are ready.",
      },
    ],
    cpgIssues: [
      "Sell sheets and trade decks repeat claims that appear on the label, so the copy on them needs the same internal review as packaging rather than being treated as sales material.",
      "Retailer and marketplace listing images are built to strict specifications — dimensions, background, text limits — which change periodically, so you need the editable file to keep listings compliant with the account's current spec.",
      "In-store point-of-sale material is printed to the retailer's own dimensions and mounting rules, and the specification comes from the account rather than from your designer.",
      "Case cards, shelf talkers and shippers are print jobs with minimums like any other packaging component, so an error costs a full run, not a reprint of one sheet.",
      "Distributor and broker portals need assets in specific formats and naming conventions, and a library of loose exports is what makes onboarding a new distributor slow.",
    ],
    founderMistakes: [
      "Commissioning a sell sheet with no editable file, then paying again to change one price point before a category review.",
      "Using a stock image licensed for digital use on a printed case card, and finding out from the licensor rather than the designer.",
      "Letting a designer keep the social template library, so every seasonal variation went back through them at rush rates.",
      "Never asking who actually did the illustration, and having no assignment from the subcontractor who owned it.",
    ],
    negotiationPoints: [
      "Ask for editable working and template files as a named deliverable, not just final exports.",
      "Require a written list of third-party assets and confirmation that the licences cover print and packaging where relevant.",
      "Define a revision round precisely and price additional rounds up front.",
      "Require written assignment from any subcontractor who touches the work.",
      "Limit portfolio rights until after the asset has been used publicly.",
    ],
    redFlags: [
      "Template and working files excluded from the deliverables.",
      "No assignment language, or ownership passing only on final payment with no further-assurances obligation.",
      "Unlimited revisions with no definition, paired with an hourly rate.",
      "Stock or font usage the designer cannot document.",
    ],
    beforeYouSign: [
      "A named asset list with formats, dimensions and the destination for each.",
      "Confirmation of who else will work on the files and whether they are employees or subcontractors.",
    ],
    related: [
      "packaging-design-agreement",
      "branding-agency-agreement",
      "design-ownership-agreement",
      "work-for-hire-agreement",
      "ip-assignment-agreement",
      "website-development-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "website-development-agreement",
    name: "Website Development Agreement",
    category: "marketing",
    subcategory: "Design and web",
    aliases: [
      "Web Design and Development Agreement",
      "E-Commerce Build Agreement",
      "Shopify Build Agreement",
      "Site Development Contract",
    ],
    stages: ["pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Web Developer or Digital Agency",
    whenYouNeedIt:
      "Before a direct-to-consumer site is built or rebuilt, and before anyone else sets up the store, the domain, the payment processor or the analytics on your behalf.",
    whatItDoes:
      "Commissions the design and build of a website or storefront, defines what is delivered, who owns the code and the theme customisations, who holds every account the site depends on, and what happens to support and maintenance afterwards.",
    whyItMatters:
      "A CPG storefront is a small pile of accounts held together by credentials: the store platform, the domain registrar, the payment processor, the email platform, the analytics property, the review widget, the subscription app. Founders let a developer create all of them and only discover the problem when they change developers or when a card expires on an account nobody can log into. The second issue is that the consumer-facing text on the site — subscription terms, auto-renewal language, shipping and refund policy, marketing consent — is not a design deliverable. Those rules vary by state and country and change often, and a developer pasting in boilerplate is not the review that content needs.",
    majorTerms: [
      "Scope: pages, templates, integrations and migrations",
      "Ownership of custom code, theme customisations and design",
      "Licences to any third-party theme, plugin or app used",
      "Account ownership: domain, store, processor, analytics, email",
      "Launch criteria and acceptance testing",
      "Post-launch support window and bug fix obligations",
      "Ongoing maintenance fees and response times",
      "Data migration and export obligations",
      "Who supplies and approves policy and legal page content",
      "Handover documentation and credentials transfer",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of custom code",
        why: "A developer who licenses their custom work rather than assigning it can charge to keep the site running, or refuse to let another firm touch it.",
      },
      {
        clause: "Account ownership and credentials",
        why: "Accounts created under the developer's email leave you locked out of your own domain, store or analytics when the relationship ends.",
      },
      {
        clause: "Third-party themes and apps",
        why: "A premium theme licensed to the developer's account may not transfer, so the site you paid for is running on a licence you do not hold.",
      },
      {
        clause: "Responsibility for policy pages",
        why: "If the agreement makes the developer responsible for the terms, privacy, shipping and subscription pages, you are relying on a designer for content that needs a lawyer.",
      },
      {
        clause: "Acceptance and launch criteria",
        why: "Without written acceptance criteria, 'done' is a negotiation, and the final payment argument happens on launch day.",
      },
      {
        clause: "Data export on exit",
        why: "Customer records, order history and subscriber lists need to leave with you in usable form, and the obligation belongs in writing.",
      },
    ],
    cpgIssues: [
      "Subscription and auto-renewal presentation, cancellation flow and consent language for email and text marketing are consumer-facing rules that vary by jurisdiction and change — treat the developer's build as the mechanism and get the wording reviewed by counsel.",
      "Perishable and temperature-sensitive products need shipping logic a generic build does not have: cut-off days so nothing sits in a weekend transit, regional carrier rules, insulated pack surcharges and a stated policy on heat-damaged deliveries.",
      "Product page copy for supplements and functional beverages repeats the claim set from the label, so the site content needs the same review path as packaging rather than being written by a copywriter to sell.",
      "Customer reviews and user photos displayed on product pages can carry claims the brand cannot make itself, and the review widget's moderation settings are a marketing decision, not a developer default.",
      "Marketplace and retailer traffic means listing images and site imagery need to stay consistent across channels you do not control, so asset naming and a single source of truth matter more than they do for a single storefront.",
      "Lot codes, best-by dates and allergen statements shown on the site have to match what is actually in the pack, and a static product page quietly goes stale after a reformulation.",
    ],
    founderMistakes: [
      "Letting the developer register the domain on their own account, then losing days of downtime during a handover dispute.",
      "Launching with template policy pages copied from another brand, including a refund policy the fulfilment operation could not actually deliver.",
      "Buying a premium theme through the developer's licence and being unable to receive updates after the engagement ended.",
      "Building a subscription flow with no cancellation path, then handling cancellations manually by email at volume.",
      "Never getting the analytics property transferred, and starting from zero historical data with the next agency.",
    ],
    negotiationPoints: [
      "Require every account to be created under the brand's own credentials, with the developer added as a collaborator.",
      "Ask for a present assignment of custom code and design, plus a licence to anything genuinely pre-existing that the site depends on.",
      "List the third-party themes, apps and plugins and confirm each licence is held in the company's name.",
      "Put written acceptance criteria and a defined post-launch bug-fix window in the agreement.",
      "Make the developer responsible for implementing policy content you supply, and keep responsibility for the content itself with you and counsel.",
      "Require a documented handover: credentials, architecture notes and an export of customer and order data.",
    ],
    redFlags: [
      "Custom code licensed rather than assigned, or tied to an ongoing maintenance retainer.",
      "Accounts held under the developer's email with no transfer obligation.",
      "The developer offering to supply terms, privacy and subscription policies as part of the build.",
      "No acceptance criteria and no defined support period after launch.",
      "No obligation to export customer and order data on termination.",
    ],
    beforeYouSign: [
      "A list of every account the site will need and who will own each one.",
      "Your policy content — shipping, refunds, subscriptions, marketing consent — drafted or reviewed by counsel, ready to be implemented rather than invented.",
      "Written acceptance criteria and a launch date tied to something real, such as a first production run arriving.",
    ],
    related: [
      "website-terms-of-use",
      "privacy-policy",
      "subscription-terms",
      "graphic-design-agreement",
      "marketing-agency-agreement",
      "ip-assignment-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The consumer-facing content a storefront carries — subscription and auto-renewal presentation, marketing consent, privacy and refund terms — is governed by rules that differ by jurisdiction and change frequently, and a developer's boilerplate is not a substitute for review.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "social-media-management-agreement",
    name: "Social Media Management Agreement",
    category: "marketing",
    subcategory: "Agencies",
    aliases: [
      "Community Management Agreement",
      "Social Media Agency Agreement",
      "Content Calendar Agreement",
      "Organic Social Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Social Media Agency or Manager",
    whenYouNeedIt:
      "Before handing over the brand's social accounts, and before anyone creates a new account on the brand's behalf. The account and the handle are brand assets; treat the handover like giving someone keys.",
    whatItDoes:
      "Sets who posts what and how often, who responds to comments and messages, who owns the content produced and the accounts it is posted from, and how the accounts and the content library come back to you.",
    whyItMatters:
      "The recurring failure is small and stupid and very expensive: the agency created the account with their own email and their own two-factor device, and when the relationship ends the brand has no way to recover a handle with a real following. The second failure is the content library. A year of posts, reels and photography made by an agency belongs to whoever the agreement says it belongs to, and a licence limited to the term means the brand cannot reuse a year of its own best-performing content — or run any of it as paid media. The third is that the inbox is a customer service channel and a signal channel at once, and consumer messages reporting a reaction to a product should never sit unread in an agency's queue.",
    majorTerms: [
      "Platforms covered and posting frequency",
      "Ownership of accounts, handles and admin access",
      "Ownership and assignment of content produced",
      "Whether the licence covers paid amplification of organic posts",
      "Community management scope and response times",
      "Escalation path for complaints and product safety messages",
      "Approval workflow and turnaround",
      "Reporting and the metrics reported",
      "Content library handover on termination",
      "Term, notice and offboarding obligations",
    ],
    clausesToWatch: [
      {
        clause: "Account ownership and admin rights",
        why: "An account created under an agency's credentials, with two-factor on their device, can be genuinely unrecoverable when the relationship ends badly.",
      },
      {
        clause: "Content ownership and paid usage",
        why: "Organic posting rights are not advertising rights, and the post that performs best is exactly the one you will want to put budget behind.",
      },
      {
        clause: "Escalation of consumer complaints",
        why: "A message describing a reaction to a supplement or beverage is not a customer service ticket, and it needs to reach the brand the same day.",
      },
      {
        clause: "Approval workflow",
        why: "Without a defined approval path and a deadline, either everything stalls waiting on the founder or nothing is reviewed before it goes live.",
      },
      {
        clause: "Reposting user content",
        why: "A repost is a use of someone else's content and likeness, and doing it at volume without permission creates a stream of small exposures.",
      },
      {
        clause: "Handover of the content library",
        why: "Without an obligation to deliver the raw and edited files, you leave with the posts visible on a feed and nothing you can re-edit or re-run.",
      },
    ],
    cpgIssues: [
      "Consumer messages describing an adverse reaction, a foreign object or an off-taste have to reach the brand quickly and be logged, and you should confirm with counsel what your own record-keeping obligations are for that category of message.",
      "Comment threads under a supplement or functional beverage post fill with other people's health claims, and how those are moderated or left standing is a brand decision that belongs in the agreement.",
      "Giveaways and contests run on social have their own rules requirements that vary by jurisdiction, so the agency should not be launching one without a written set of rules that counsel has seen.",
      "Retailer launch announcements are frequently under embargo or tied to a set date, and an agency posting 'now at' before the product is actually on shelf creates a problem with the account.",
      "Content showing the product being made, or shot at a co-manufacturer's facility, may breach your confidentiality obligations to that facility.",
      "Posts referencing a limited flavour or a seasonal SKU outlive the inventory, so the content calendar should track what is actually available rather than what launched.",
    ],
    founderMistakes: [
      "Letting an agency create the brand's account under their own login, then losing a handle with a real audience when the contract ended.",
      "Discovering that a year of agency-produced content was licensed for organic use only, so none of it could be put behind paid budget.",
      "Leaving direct messages entirely with the agency and finding a month-old message describing a reaction to the product sitting unread.",
      "Running a giveaway written by the agency with no rules page, and having to unwind it after entries came in from places the product could not ship to.",
      "Approving a content calendar without checking inventory, and promoting a flavour that was out of stock for six weeks.",
    ],
    negotiationPoints: [
      "Own every account yourself and add the agency as an administrator, never the reverse.",
      "Ask for a present assignment of all content produced, plus delivery of raw and editable files on a monthly cadence rather than at the end.",
      "Make paid amplification an express permitted use of everything produced under the agreement.",
      "Set a written escalation procedure with a same-day standard for messages about product safety or reactions.",
      "Agree an approval workflow with a deemed-approval deadline so the calendar does not stall on one person.",
      "Require the content library, including raws, to be delivered before the final invoice is paid.",
    ],
    redFlags: [
      "Accounts or handles held in the agency's name, or two-factor tied to an agency device.",
      "Content licensed for the term rather than assigned.",
      "No escalation path for consumer complaints or product safety messages.",
      "Giveaways or contests run without written rules.",
      "No obligation to hand over raw files and the content library on exit.",
    ],
    beforeYouSign: [
      "A written inventory of accounts, handles and who currently controls each.",
      "The escalation procedure for consumer complaints, agreed and documented.",
      "A monthly content handover requirement built into the payment schedule.",
    ],
    related: [
      "marketing-agency-agreement",
      "content-creator-agreement",
      "ugc-creator-agreement",
      "influencer-agreement",
      "contest-rules",
      "sweepstakes-rules",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "advertising-agency-agreement",
    name: "Advertising Agency Agreement",
    category: "marketing",
    subcategory: "Agencies",
    aliases: [
      "Media Agency Agreement",
      "Paid Media Agreement",
      "Performance Marketing Agreement",
      "Creative and Media Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Advertising or Media Agency",
    whenYouNeedIt:
      "Before an agency starts buying media on your behalf or producing advertising you intend to run at scale — particularly once spend is large enough that the fee structure and the media terms matter more than the creative fee.",
    whatItDoes:
      "Covers creative production and media buying: what the agency makes, what it buys, how it is compensated, who carries the liability for media invoices, and what rights you hold in the advertising once it is made.",
    whyItMatters:
      "Advertising agreements fail at the point of success. You find a creative that works, you want to scale it and extend it, and then you meet the licences underneath: the music was cleared for one year, the talent's usage window covered a single campaign, the stock footage was licensed for digital and not for connected television. The asset is performing and you cannot keep running it, and the renewal price is set by someone who can see your spend. The second issue is money flow — whether the agency buys media as your agent or as a principal changes who is on the hook when an invoice goes unpaid and whether a rebate belongs to you.",
    majorTerms: [
      "Creative production scope and separate media buying scope",
      "Fee model: retainer, project, percentage of spend, performance element",
      "Whether the agency buys media as agent or as principal",
      "Media invoicing, payment terms and liability for unpaid media",
      "Rebates, incentives and volume credits, and who keeps them",
      "Ownership and assignment of creative",
      "Usage terms for music, stock, talent and voiceover",
      "Renewal pricing for extending talent and music licences",
      "Approval process for claims and creative",
      "Reporting, data access and platform account ownership",
      "Term, notice and post-termination rights to keep running the work",
    ],
    clausesToWatch: [
      {
        clause: "Talent and music usage windows",
        why: "The best-performing ad is the one you most want to keep running, and a one-year music or talent term hands the renewal negotiation to the other side at exactly that moment.",
      },
      {
        clause: "Agent versus principal media buying",
        why: "When the agency buys as principal, margin can sit inside the media price rather than in a disclosed fee, and rebates may never reach you.",
      },
      {
        clause: "Liability for unpaid media",
        why: "If the agency fails to pay a platform or a publisher, the question of who the debt belongs to should be settled in the contract, not afterwards.",
      },
      {
        clause: "Post-termination usage rights",
        why: "Without an express right to keep running existing creative after the relationship ends, a campaign can go dark on the day you switch agencies.",
      },
      {
        clause: "Claims approval",
        why: "The brand carries the exposure for what an ad says about a product, so the agreement should require documented brand approval before any claim runs.",
      },
      {
        clause: "Ownership of accounts and reporting data",
        why: "Reporting delivered as a slide deck is not data; you need access to the account itself and an export of what was spent and what it produced.",
      },
    ],
    cpgIssues: [
      "Retail media — marketplace ads, retailer portals and delivery platforms — is bought inside accounts the retailer controls, and the account setup and permissions need to be in the brand's name from the start.",
      "Ads that make a functional, performance or health-related statement about a beverage or supplement carry exposure for the brand, so the substantiation file and the approval trail matter more than the creative brief.",
      "Advertising that drives demand ahead of supply is a real risk in CPG: a campaign that outruns a production run leaves paid traffic hitting an out-of-stock page and burns the account's learning.",
      "Retailer co-op and market development funds usually require proof of performance in a specific format and by a deadline, and unclaimed funds are simply lost.",
      "Geography matters when the product is a consumable: ads served where you cannot ship, or where the SKU is not distributed, spend budget on demand you cannot fill.",
      "Creative that shows a pack becomes obsolete at every label revision, so plan an asset refresh alongside artwork changes rather than discovering old packaging in a live ad.",
    ],
    founderMistakes: [
      "Scaling a winning video and then losing it because the music licence ran twelve months and the renewal was quoted against the campaign's performance.",
      "Letting the agency buy media on its own credit line, then having ads paused over an invoice dispute the brand knew nothing about.",
      "Never asking whether rebates existed, and leaving volume credits with the agency for two years of spend.",
      "Approving a claim-heavy script in a review call with no written record of who approved what.",
      "Switching agencies with no post-termination usage right, and taking every ad down on the transition date.",
    ],
    negotiationPoints: [
      "Ask for talent, music and stock licences to run perpetually where possible, or price the extension in the original deal.",
      "Require full disclosure of the buying model, any rebates or incentives, and how they are treated.",
      "Secure an express, perpetual right to continue running creative produced under the agreement after termination.",
      "Keep every advertising account in the brand's ownership with the agency as a user, and require raw data export on request.",
      "Put a written claims approval workflow in the agreement, with the brand as the final approver.",
      "Tie part of the fee to agreed performance outcomes rather than to spend alone.",
    ],
    redFlags: [
      "Talent or music cleared for a term shorter than the expected life of the campaign, with no agreed renewal price.",
      "Media bought as principal with no disclosure of the margin or the rebates.",
      "No post-termination right to keep running existing creative.",
      "Reporting available only as agency-prepared summaries with no account access.",
      "The agency positioned as the approver of claim language.",
    ],
    beforeYouSign: [
      "A written schedule of every third-party licence inside the creative, with its term and its renewal cost.",
      "Confirmation of who owns each advertising account and who holds admin.",
      "Your substantiation file for any claim the advertising will make, and an agreed approval path.",
    ],
    related: [
      "marketing-agency-agreement",
      "videography-agreement",
      "photography-agreement",
      "influencer-agreement",
      "social-media-management-agreement",
      "work-for-hire-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "photography-agreement",
    name: "Photography Agreement",
    category: "marketing",
    subcategory: "Production and content",
    aliases: [
      "Commercial Photography Agreement",
      "Product Photography Agreement",
      "Shoot Agreement",
      "Photographer Contract",
    ],
    stages: ["pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Photographer or Photo Studio",
    whenYouNeedIt:
      "Before any commercial shoot — product on white, lifestyle, packaging hero, founder portraits, in-store or event coverage — and before booking a studio, a stylist or talent for it.",
    whatItDoes:
      "Sets the terms of the shoot and, critically, the rights that come out of it: whether the brand owns the images or licenses them, in which channels and territories, for how long, and whether the files can be edited.",
    whyItMatters:
      "The default in commercial photography is a licence, not a transfer, and licences are written channel by channel. That default collides badly with how CPG images actually travel. A brand's product shot ends up on the website, on a marketplace listing, in a retailer's own portal, in a distributor's catalogue, on a sell sheet, in a paid ad, on a shelf talker and sometimes on the pack — and most of those destinations are outside the brand's control, uploaded by an account or a partner using assets you sent them. A licence scoped to 'social and web' does not cover that. The other half is editing: retailers and marketplaces have their own image specifications, so you need raw files and an express right to crop, retouch and recolour, or every spec change is a new shoot.",
    majorTerms: [
      "Whether the deal is an assignment or a licence",
      "Named channels: web, paid media, marketplaces, retailer portals, trade, packaging, out-of-home",
      "Territory and duration of any licence",
      "Right to edit, crop, retouch, recolour and create derivatives",
      "Delivery of raw files, selects and outtakes",
      "Number of images, shot list and usage per image",
      "Who books and pays for studio, stylist, props and talent",
      "Who obtains model, property and location releases",
      "Exclusivity — whether the images can be licensed to anyone else",
      "Credit requirements and where they apply",
      "Reshoot terms if the product or pack changes",
    ],
    clausesToWatch: [
      {
        clause: "Channel scope of the licence",
        why: "Assets sent to a distributor or uploaded to a retailer portal are being used in channels the brand does not control, and a narrow channel list makes routine trade activity a breach.",
      },
      {
        clause: "Paid media",
        why: "A licence covering social media frequently excludes paid amplification, which is the use that has budget attached to it.",
      },
      {
        clause: "Packaging use",
        why: "On-pack imagery is a separate and more expensive grant, and an expired grant reaches shelf inside printed inventory you cannot recall.",
      },
      {
        clause: "Term of the licence",
        why: "A one- or two-year term on hero product photography expires while the SKU is still selling, and marketplace listings go stale or come down.",
      },
      {
        clause: "Right to edit and raw file delivery",
        why: "Without raws and an editing right, every retailer template change, flavour extension and pack refresh means going back to the photographer.",
      },
      {
        clause: "Who secures releases",
        why: "If the photographer is not obliged to obtain model, property and location releases, the brand carries the risk for every recognisable face and place in frame.",
      },
      {
        clause: "Reshoot obligation on pack change",
        why: "Labels change more often than founders expect, and an agreed rate for reshooting the current pack is much cheaper agreed now than later.",
      },
    ],
    cpgIssues: [
      "Marketplace and retailer listing images have strict, published specifications — dimensions, background, fill percentage, text limits — and they are revised periodically, so both editing rights and raw files are operationally necessary.",
      "Product photography goes obsolete at every label revision, so tie the licence to the life of the pack generation rather than to a calendar term where you can.",
      "Distributors, brokers and retailers republish your images in their own catalogues and portals, which is redistribution the brand does not control and which the licence has to permit.",
      "Food styling and serving suggestions can imply something about the product's contents or benefits, so the marketing review of a shot is separate from, and additional to, the licence question.",
      "Props, competitor products, recognisable venues and branded equipment in frame carry clearance issues the photography licence does not solve.",
      "Shooting inside a co-manufacturer's facility exposes their line configuration and process, which is usually covered by your confidentiality obligations to them.",
      "Trade materials — sell sheets, distributor decks, booth graphics, shelf talkers — are a distinct use that consumer-focused licences routinely omit.",
    ],
    founderMistakes: [
      "Running lifestyle images as paid ads for a year before learning the licence covered organic use only.",
      "Building marketplace listings on images with a fixed licence term, then having to reshoot to keep the listings live.",
      "Sending the image library to a new distributor without checking whether the licence permitted third-party republication.",
      "Putting a shoot image on the pack under a licence that never mentioned packaging, discovered after a full label print run.",
      "Accepting flattened selects only, and being unable to re-crop for a retailer's updated image spec.",
      "Assuming the photographer had releases for everyone in frame because the shoot looked professional.",
    ],
    negotiationPoints: [
      "Buy an outright assignment for hero product and packaging imagery; licence the lifestyle library if budget is tight.",
      "Name every channel explicitly, including paid media, marketplaces, retailer and distributor portals, trade materials, packaging and out-of-home.",
      "Add an express right for third parties in your distribution chain to reproduce the images in their own catalogues and listings.",
      "Ask for a perpetual term, or at minimum a term tied to the life of the pack generation.",
      "Require delivery of raw files and outtakes plus an unrestricted right to edit and create derivatives.",
      "Make the photographer responsible for obtaining and delivering all model, property and location releases.",
      "Agree a per-image or per-day reshoot rate now for the inevitable pack change.",
    ],
    redFlags: [
      "A licence silent on paid media or on packaging.",
      "A term shorter than the expected life of the SKU.",
      "No right to sublicense or permit republication by retailers and distributors.",
      "No obligation on the photographer to obtain releases.",
      "Raw files withheld, or delivery limited to flattened selects.",
      "Mandatory credit on every use, including packaging and paid advertising.",
    ],
    beforeYouSign: [
      "A written list of every destination the images will reach, including retailer portals and partner catalogues you do not control.",
      "Confirmation of who is obtaining and delivering model, property and location releases.",
      "The delivery specification: file formats, raw material, retouching scope and turnaround.",
      "A decision on whether any image is destined for packaging, made before the shoot rather than after.",
    ],
    related: [
      "content-release",
      "model-release",
      "videography-agreement",
      "copyright-assignment",
      "ip-assignment-agreement",
      "packaging-design-agreement",
    ],
    attorneyReview: "yes",
    templateId: "photography-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "videography-agreement",
    name: "Videography Agreement",
    category: "marketing",
    subcategory: "Production and content",
    aliases: [
      "Video Production Agreement",
      "Film Production Agreement",
      "Commercial Production Contract",
      "Content Shoot Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Videographer or Production Company",
    whenYouNeedIt:
      "Before a video shoot of any size — a brand film, a product explainer, a founder story, an ad cut for paid media, or a day of content capture at a shoot or an event.",
    whatItDoes:
      "Commissions video production and sets who owns the finished films, the raw footage and the editor's project files, plus the terms of every third-party element inside them: music, talent, voiceover, stock and motion graphics.",
    whyItMatters:
      "Video is the format where the layers underneath cost the most. A finished ad is a stack of separate rights — the footage, the music, the voice, the talent's appearance, the stock inserts, the graphics template — and each can carry its own term and its own channel restriction. A brand can own the film outright and still be unable to run it as a paid ad in a new market. The practical CPG failure is the cutdown: paid media needs the same story in six aspect ratios and four lengths, and every one of those is a derivative work. Without the raw footage, the project file and an unrestricted right to edit, every new placement goes back through the production company at their rate.",
    majorTerms: [
      "Deliverables: masters, cutdowns, aspect ratios and lengths",
      "Ownership or licence of the finished films",
      "Delivery of raw footage, project files and graphics templates",
      "Music licensing: term, territory, channels and renewal cost",
      "Talent and voiceover usage windows and renewal terms",
      "Right to edit, re-cut and create derivative versions",
      "Channels covered, including paid media and connected television",
      "Crew, kit, location and insurance responsibility",
      "Model, property and location releases and who obtains them",
      "Reshoot and pick-up terms",
      "Payment schedule tied to file delivery",
    ],
    clausesToWatch: [
      {
        clause: "Music licence term and channels",
        why: "Library and custom music are licensed by use and duration, and a short term on the track under your best ad hands the renewal negotiation to someone who can see it working.",
      },
      {
        clause: "Talent usage windows",
        why: "On-camera talent is frequently engaged for a defined campaign period, and the ad has to come down when the window closes regardless of how it is performing.",
      },
      {
        clause: "Delivery of raws and project files",
        why: "Without them, every new cutdown, aspect ratio, subtitle version and pack-shot swap is a paid job at the production company's rate.",
      },
      {
        clause: "Right to create derivatives",
        why: "Cutdowns, re-cuts and localised versions are derivative works, and a licence to the delivered master alone does not permit them.",
      },
      {
        clause: "Paid media and connected television",
        why: "Licences written for organic or web use routinely exclude paid placement and broadcast-adjacent channels, which is where the budget goes.",
      },
      {
        clause: "Release responsibility",
        why: "Everyone recognisable on camera, and every private location, needs a release, and the production company is better placed to collect them on the day than you are afterwards.",
      },
    ],
    cpgIssues: [
      "Pack shots date the moment the label changes, so keep the pack as a separate, replaceable layer in the project file rather than baked into the edit.",
      "Consumption on camera reads as personal endorsement, which matters more for supplements and functional beverages than for most categories, and it changes what the on-screen talent's release needs to cover.",
      "Recipe, preparation and serving-suggestion footage can imply something about the product's contents or benefits, so it needs the same review path as a claim on the label.",
      "Shooting on a production line or inside a co-manufacturer's facility reveals equipment, line speed and process that are usually confidential to that facility.",
      "Video shot at a retail location shows other brands, shelf sets and store branding, and stores generally require permission before any commercial shoot on their floor.",
      "Product used on set has to be shot-ready and on-code, and a shoot scheduled against a production run frequently ends up filming pre-production packaging that never reaches shelf.",
    ],
    founderMistakes: [
      "Commissioning a brand film with no raw footage delivery, then paying the production company again for every vertical cutdown the media plan needed.",
      "Scaling an ad that was working until the music licence expired, and taking it down mid-flight.",
      "Booking talent for a single campaign window and being unable to keep the best-performing creative live.",
      "Filming at a co-packer without permission and creating a confidentiality problem with the plant.",
      "Shooting a full campaign against pre-production packaging that changed before launch, making every asset unusable.",
    ],
    negotiationPoints: [
      "Require delivery of raw footage, editor project files and graphics templates as a named deliverable tied to final payment.",
      "Buy music and voiceover on perpetual, all-media terms where the budget allows, and price renewals up front where it does not.",
      "Secure an express right to edit, re-cut, subtitle, localise and create derivative versions without further approval.",
      "Name every channel including paid social, marketplaces, connected television, trade and in-store screens.",
      "Make the production company responsible for obtaining and delivering every release on the shoot day.",
      "Keep the pack shot as an isolated layer, and agree a rate now for swapping it when the label changes.",
    ],
    redFlags: [
      "Raw footage and project files excluded from the deliverables.",
      "Music or talent licensed for a term shorter than the campaign's expected life, with no agreed renewal price.",
      "No right to create derivative cuts.",
      "Paid media excluded or unaddressed in the usage grant.",
      "No obligation to obtain releases, or releases collected informally after the shoot.",
    ],
    beforeYouSign: [
      "The media plan, so the channel list and the licence terms are written against where the video will actually run.",
      "A written schedule of every third-party element — music, stock, voice, graphics — with its licence term.",
      "Confirmation that the packaging being filmed is the artwork that will actually be on shelf.",
    ],
    related: [
      "photography-agreement",
      "content-release",
      "model-release",
      "advertising-agency-agreement",
      "ugc-creator-agreement",
      "ip-assignment-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "influencer-agreement",
    name: "Influencer Agreement",
    category: "marketing",
    subcategory: "Creators and influencers",
    aliases: [
      "Creator Partnership Agreement",
      "Paid Partnership Agreement",
      "Sponsored Post Agreement",
      "Influencer Marketing Contract",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Influencer or their Management",
    whenYouNeedIt:
      "Before any paid or gifted collaboration where a creator will post about your product — including the seeding programme you think is too small to paper, because a gifted post with no agreement gives you no rights to anything.",
    whatItDoes:
      "Sets the deliverables, the compensation, the posting schedule and, above all, the three separate rights the brand may want in the content: organic posting on the creator's channel, the brand's own use of the content in paid advertising, and whitelisting — running ads from the creator's own handle.",
    whyItMatters:
      "This is the single most expensive gap in CPG influencer deals: brands buy a post rather than an asset. Organic usage, paid-ad usage and whitelisting are three separate grants with three separate durations, and a standard creator quote covers only the first. So the sequence founders keep living through goes: run a batch of creators, find the one video that actually converts, discover you have no right to put budget behind it, and go back to negotiate for the asset at the moment its value is proven and visible. A brand that finds a winning creative and cannot scale it as paid media has bought nothing. Price all three grants at the start, when the content is worth the same as everyone else's.",
    majorTerms: [
      "Deliverables: number, format, platform and posting dates",
      "Compensation: flat fee, gifted product, affiliate share or hybrid",
      "Organic usage: minimum time the post must stay live",
      "Paid usage rights: the brand running the content as its own ad, and for how long",
      "Whitelisting or partnership-ad permission from the creator's handle, and its duration",
      "Territory and platforms covered by each grant",
      "Category exclusivity and its scope and duration",
      "Approval rights and revision rounds before posting",
      "Disclosure obligations for the sponsored relationship",
      "Content brief, required talking points and a do-not-say list",
      "Takedown, removal and archive rights",
      "Conduct provisions and termination for reputational events",
      "Reporting: metrics, screenshots and code performance",
    ],
    clausesToWatch: [
      {
        clause: "Scope and duration of paid usage rights",
        why: "Usage that expires mid-flight forces a live, converting ad off the platform and hands the renewal negotiation to the creator at the worst moment.",
      },
      {
        clause: "Whitelisting permission",
        why: "Running an ad from the creator's own handle usually outperforms the same content from the brand's, and it needs an explicit, separate grant plus platform-level access.",
      },
      {
        clause: "Minimum live period and deletion",
        why: "A creator free to delete after thirty days can remove the post your ad is anchored to and end the campaign without breaching anything.",
      },
      {
        clause: "Category exclusivity",
        why: "Written too narrowly it lets the creator post for a directly competing product a week later; written too broadly it prices you out of creators who work across the category.",
      },
      {
        clause: "Approval rights and turnaround",
        why: "No approval means the creator describes your product in their own words with no review; unlimited approval with no deadline stalls the whole campaign.",
      },
      {
        clause: "Disclosure of the paid relationship",
        why: "Disclosure obligations attach to sponsored content and the brand generally carries responsibility for requiring them, so the requirement belongs in the contract and in the brief.",
      },
      {
        clause: "Management and network terms layered on top",
        why: "A creator's agency or network may impose its own usage and payment terms, and those can quietly override what the creator agreed with you.",
      },
    ],
    cpgIssues: [
      "The brand carries the exposure for how a creator describes a supplement or functional beverage, so a written brief with required language and an explicit do-not-say list matters more here than in most categories — and the claim set the brief permits should be reviewed by counsel before it goes to a single creator.",
      "Creators frequently describe a product's effect on them personally, which reads as a claim about the product regardless of how it is framed, and the agreement should give the brand approval over that language before posting.",
      "Audience geography has to match distribution: a creator whose following sits where the SKU is not stocked and where you cannot ship generates demand you cannot fill.",
      "Product has to reach the creator with real shelf life on it, in a condition worth filming — seeding shipments that arrive warm, dented or close to code produce content you cannot use.",
      "Retailer call-outs are a timing risk: a creator posting 'find it at' before the SKU is physically on shelf sends an audience to an empty facing and irritates the account.",
      "Consumption on camera and any before-and-after framing sit much closer to endorsement than a product mention does, and for supplements that difference is the one to get reviewed.",
      "Allergen and ingredient mentions get made casually on camera; the brief should say what the creator must not assert about who the product is suitable for.",
    ],
    founderMistakes: [
      "Paying for a post, finding the one video in the batch that actually converted, and having no right to run it as an ad — then paying several times the original fee for usage once the performance was visible.",
      "Buying paid usage but not whitelisting, and losing most of the performance because the ad had to run from the brand's handle.",
      "Running a gifted seeding programme with no written agreement at all, so the strongest content in the brand's history could not be used for anything.",
      "Agreeing a six-month usage term, scaling the creative, and having to pull a profitable ad on the expiry date.",
      "Never setting a minimum live period, and having the anchor post deleted while the ad was running against it.",
      "Sending a creator product with four weeks of shelf life left and receiving content that showed a near-expiry date on camera.",
    ],
    negotiationPoints: [
      "Price organic, paid usage and whitelisting as three separate line items in the first negotiation, before anyone knows which content will perform.",
      "Ask for paid usage in perpetuity, or for a defined extension price agreed now rather than at renewal.",
      "Secure whitelisting access explicitly, including the platform-level permission that makes it work, not just a sentence saying it is allowed.",
      "Set a minimum live period for the organic post and a notice requirement before any deletion.",
      "Define category exclusivity by product type and duration, listing what is included and what is not.",
      "Require a written brief with mandatory language and a do-not-say list, plus brand approval before posting with a deemed-approval deadline.",
      "Require disclosure of the paid relationship in the format the platform provides, and make it a condition of payment.",
      "Ask for raw or unedited footage where the content is likely to be re-cut for ads.",
    ],
    redFlags: [
      "A fee that covers posting only, with usage rights described as available later.",
      "Usage rights shorter than the expected life of the campaign, with no agreed renewal price.",
      "No minimum live period and no notice requirement before deletion.",
      "No disclosure obligation anywhere in the agreement.",
      "No approval right over how the product is described, on a product with functional or health-adjacent positioning.",
      "Management terms referenced but not attached, so the actual usage grant is in a document you have not seen.",
      "No conduct provision on a long-term or high-value deal.",
    ],
    beforeYouSign: [
      "A decision on whether this content is likely to become paid media, made before the fee is agreed rather than after the results come in.",
      "The written brief, including required language, the do-not-say list and the claim set counsel has cleared.",
      "Confirmation of who actually holds the rights — the creator, their management or a network — and a copy of any terms they layer on.",
      "Confirmation that the product will reach the creator on code and in shippable condition, in a market where the SKU is available.",
    ],
    related: [
      "ugc-creator-agreement",
      "content-creator-agreement",
      "ambassador-agreement",
      "influencer-affiliate-agreement",
      "influencer-disclosure",
      "nil-release",
    ],
    attorneyReview: "yes",
    templateId: "influencer-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "content-creator-agreement",
    name: "Content Creator Agreement",
    category: "marketing",
    subcategory: "Creators and influencers",
    aliases: [
      "Creator Services Agreement",
      "Freelance Content Agreement",
      "Content Production Agreement",
      "Brand Content Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Content Creator",
    whenYouNeedIt:
      "When you are hiring someone for their ability to make content rather than for their audience — a creator producing recipe videos, photography, tutorials or social assets that will live on the brand's own channels.",
    whatItDoes:
      "Engages a creator to produce content for the brand, defines the volume and cadence, and settles ownership of the finished assets, the raw material and the right to run them anywhere.",
    whyItMatters:
      "This is a production deal wearing creator clothing, and treating it as an influencer deal loses the point. You are not buying reach; you are buying a library, and a library is only worth what you can do with it. The failure mode is a rolling monthly arrangement with no written terms, twelve months of content on a shared drive, and no clarity about whether the brand can run any of it as paid media, edit it, hand it to a retailer, or keep using it after the creator moves on. The second issue is that the creator is shooting in their own space with their own props and often their own family in frame, which brings clearance questions a studio shoot would have handled on the day.",
    majorTerms: [
      "Volume, cadence and format of content per period",
      "Ownership or licence of the finished content",
      "Delivery of raw files and project files",
      "Paid media rights and their duration",
      "Whether the creator also posts on their own channels",
      "Approval and revision process",
      "Product supply, shipping and reimbursement",
      "Props, locations and third-party elements in frame",
      "Releases for anyone appearing in the content",
      "Term, notice and what happens to the library on exit",
    ],
    clausesToWatch: [
      {
        clause: "Assignment versus term-limited licence",
        why: "A licence that ends with the engagement means a year of content becomes unusable on the day the creator stops working with you.",
      },
      {
        clause: "Paid media rights",
        why: "Content made for organic channels is often licensed for exactly that, and the highest-performing asset is the one you will want to put budget behind.",
      },
      {
        clause: "Raw files and project files",
        why: "Without the raw material you cannot re-cut for a new placement, swap a pack shot after a label change, or fix a claim in the voiceover.",
      },
      {
        clause: "People appearing in the content",
        why: "Creators shooting at home routinely include partners, children and friends, and each of those needs a release before the content can run commercially.",
      },
      {
        clause: "Third-party items in frame",
        why: "Competitor products, branded kitchenware and recognisable artwork in a shot can constrain where the content can be used.",
      },
      {
        clause: "Exclusivity",
        why: "A creator producing near-identical content for a competitor next month dilutes the library you paid for, unless the agreement addresses it.",
      },
    ],
    cpgIssues: [
      "Recipe and preparation content implies things about the product's use and suitability, so it needs the same review path as a label claim rather than being treated as lifestyle content.",
      "Creators shoot with whatever pack they have on hand, and a library shot against an old label becomes unusable at the next artwork change — specify which pack generation is being filmed.",
      "Content showing the product being consumed for a functional benefit sits close to endorsement, and the brand carries exposure for what is said about it on camera.",
      "Product sent to a creator's home has no cold chain and no handling control, which matters for refrigerated, high-protein and short-shelf-life items.",
      "Allergen and dietary framing gets made casually in recipe content, and a creator calling a product suitable for a particular diet may be asserting more than the label supports.",
      "Content that references availability at a specific retailer ages badly as distribution shifts, so build the library around evergreen framing.",
    ],
    founderMistakes: [
      "Running a monthly content arrangement for a year on an email thread, then finding no written basis to use any of it in paid media.",
      "Receiving only exported files and being unable to re-cut anything when the media plan changed.",
      "Building a library around a pack design that was superseded three months later.",
      "Using content featuring the creator's family with no releases, then having to pull the assets.",
      "Never addressing exclusivity, and watching visually identical content appear for a competing brand.",
    ],
    negotiationPoints: [
      "Ask for a present assignment of the content, or at minimum a perpetual, all-media, worldwide licence including paid.",
      "Require raw and project files delivered on the same cadence as the finished assets.",
      "Require signed releases from anyone appearing on camera, delivered with the content.",
      "Agree a modest, defined exclusivity in your category rather than a broad one that raises the price.",
      "Specify the pack generation to be filmed and agree a rate for refreshing assets after a label change.",
    ],
    redFlags: [
      "Rights that expire with the engagement.",
      "Paid media excluded or unaddressed.",
      "Raw files retained by the creator.",
      "No release process for people appearing in the content.",
      "No approval step for content describing what the product does.",
    ],
    beforeYouSign: [
      "A written content brief covering claim language, pack generation and framing.",
      "A delivery specification that names raw files and the release paperwork.",
    ],
    related: [
      "ugc-creator-agreement",
      "influencer-agreement",
      "content-release",
      "model-release",
      "social-media-management-agreement",
      "ip-assignment-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ugc-creator-agreement",
    name: "UGC Creator Agreement",
    category: "marketing",
    subcategory: "Creators and influencers",
    aliases: [
      "User-Generated Content Agreement",
      "UGC Video Agreement",
      "Creative Sourcing Agreement",
      "Ad Creative Creator Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + UGC Creator",
    whenYouNeedIt:
      "When you are commissioning content that looks like a real customer made it, specifically so the brand can run it as paid advertising. The creator usually does not post it at all.",
    whatItDoes:
      "Buys advertising creative outright: a creator films to your brief, delivers the files, and the brand owns or holds a perpetual all-media licence to run it, edit it and re-cut it.",
    whyItMatters:
      "UGC is the clearest case in this whole category where the brand should be buying an asset, not a post — and it is still where the licence gets left at twelve months because that is what the creator's rate card says. Then the ad works. It becomes the account's top performer, the entire media plan is built on it, and the expiry date arrives with the creator holding all the leverage. The other failure is quieter: without exclusivity and without raw footage, the creator can shoot a near-identical piece for a competing brand and you cannot make new cutdowns of your own. Buy it once, buy it perpetually, and get the raws.",
    majorTerms: [
      "Number of videos or images, lengths and aspect ratios",
      "Perpetual, worldwide, all-media rights including paid advertising",
      "Whether the deal is an assignment or a perpetual licence",
      "Delivery of raw footage and unedited takes",
      "Right to edit, re-cut, subtitle and create derivatives",
      "Whether whitelisting from the creator's handle is included",
      "Exclusivity in the category and its duration",
      "Approval, revision rounds and reshoot terms",
      "Product supply and reimbursement",
      "Creator's appearance release and releases for anyone else on camera",
      "Disclosure obligations if the creator also posts the content",
    ],
    clausesToWatch: [
      {
        clause: "Licence duration",
        why: "A twelve-month term on ad creative expires precisely when the asset has proven itself, and the renewal is negotiated against your own performance data.",
      },
      {
        clause: "Right to edit and create derivatives",
        why: "Ads live as cutdowns and re-cuts across placements, and a licence to the delivered file alone does not permit any of them.",
      },
      {
        clause: "Raw footage delivery",
        why: "Without the raws you cannot make new hooks, swap a pack shot after a label change, or produce a shorter cut for a new placement.",
      },
      {
        clause: "The creator's own likeness",
        why: "The creator is on camera, so the deal needs an appearance release scoped as widely as the advertising licence, including paid media and any packaging use.",
      },
      {
        clause: "Category exclusivity",
        why: "Without it, a creator can film a visually identical piece for a competitor, which is both a brand problem and a platform-duplication problem.",
      },
      {
        clause: "Whitelisting",
        why: "Running the ad from the creator's handle usually performs differently from running it from yours, and it is a separate permission with its own platform access.",
      },
    ],
    cpgIssues: [
      "UGC scripts live or die on what the creator says the product does, and the brand carries the exposure for it — a written brief with cleared language and an explicit do-not-say list is the control, and it should be reviewed by counsel before it goes out to a roster of creators.",
      "Unboxing and first-impression formats show the shipper, the pack and the date code on camera, so the product you send is the product the audience sees for the next two years.",
      "Creators film with the pack they were sent, which means a whole content batch becomes unusable at the next label revision unless you plan a refresh.",
      "Before-and-after framing and personal-results storytelling are the highest-risk formats for supplements and functional beverages, and are exactly what performs — which is why the brief, not the edit, is where the control has to sit.",
      "Product shipped to creators has no cold chain, so refrigerated and short-code items arrive in a state you cannot control and often cannot use.",
      "Creators frequently film in a kitchen containing competitor products and branded packaging, and those need to be cleared from frame in the brief rather than fixed in post.",
    ],
    founderMistakes: [
      "Buying a twelve-month licence on what became the account's best-performing ad, then paying many times the original fee to keep it live.",
      "Taking delivery of finished exports only, and being unable to produce a new hook or a shorter cut when performance decayed.",
      "Running a roster of creators off a loose brief and ending up with a batch of ads asserting benefits the brand could not support.",
      "Skipping exclusivity and seeing near-identical creative appear for a competing brand in the same feed.",
      "Never obtaining a proper appearance release, then being unable to use the creator's face in a placement the licence did not anticipate.",
      "Shipping product with six weeks of shelf life to a creator roster and receiving unusable footage of a near-expiry date code.",
    ],
    negotiationPoints: [
      "Buy perpetual, worldwide, all-media rights including paid advertising as the default, and treat anything shorter as a discount you are choosing.",
      "Require raw footage and all unedited takes as a named deliverable tied to payment.",
      "Take an express right to edit, re-cut, subtitle, localise and create derivatives without further consent.",
      "Get an appearance release from the creator scoped as widely as the content licence, and releases from anyone else on camera.",
      "Negotiate whitelisting at the same time, even if you do not use it immediately.",
      "Agree category exclusivity for a defined period rather than leaving it silent.",
      "Give creators a written brief with cleared language, required framing and a do-not-say list.",
    ],
    redFlags: [
      "A term-limited licence on content bought specifically to run as advertising.",
      "No right to edit or create derivative cuts.",
      "Raw footage withheld.",
      "No appearance release, or a release narrower than the usage grant.",
      "No brief or approval step on a product with functional or health-adjacent positioning.",
      "Silence on whether the creator can produce similar content for competitors.",
    ],
    beforeYouSign: [
      "The written brief with cleared claim language and a do-not-say list.",
      "A delivery specification naming raw footage, aspect ratios and file formats.",
      "Confirmation that the product being sent is the current pack, on code and shippable to the creator's location.",
    ],
    related: [
      "influencer-agreement",
      "content-creator-agreement",
      "content-release",
      "model-release",
      "advertising-agency-agreement",
      "work-for-hire-agreement",
    ],
    attorneyReview: "yes",
    templateId: "ugc-creator-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "ambassador-agreement",
    name: "Ambassador Agreement",
    category: "marketing",
    subcategory: "Creators and influencers",
    aliases: [
      "Brand Ambassador Contract",
      "Athlete Ambassador Agreement",
      "Community Ambassador Agreement",
      "Field Ambassador Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Ambassador",
    whenYouNeedIt:
      "When someone will represent the brand over months rather than posts — an athlete, a trainer, a chef, a local personality, or a community ambassador working events and stores.",
    whatItDoes:
      "Establishes an ongoing relationship: what the ambassador does each month, what they are paid, what they may and may not say, what the brand may do with their name, image and content, and how either side ends it.",
    whyItMatters:
      "An ambassador deal accumulates. Over a year you build a content library, an association between a person and the brand, and sometimes a face on packaging or in-store material — and all of it is governed by a document usually written for a single campaign. Two things then bite. The first is the end of the relationship: without a wind-down, live posts, printed collateral and packaging featuring someone who has moved on stay in market. The second is classification and control — an individual working set hours, at your direction, wearing your branding, is a question about worker status that varies by jurisdiction and is worth getting right before it becomes a back-pay conversation.",
    majorTerms: [
      "Term, renewal and notice",
      "Monthly deliverables: posts, appearances, events, store visits",
      "Compensation: retainer, per-appearance fee, affiliate share, product",
      "Rights in name, image, likeness and content produced",
      "Whether packaging or in-store use is included",
      "Category exclusivity and its scope",
      "Approval rights and required disclosure of the relationship",
      "Conduct and reputation provisions",
      "Expenses, travel and product allocation",
      "Post-term wind-down for live content and printed material",
      "Working status and who supplies equipment and direction",
    ],
    clausesToWatch: [
      {
        clause: "Post-term wind-down",
        why: "Without a defined wind-down, content stays live and printed collateral stays in market featuring someone no longer associated with the brand.",
      },
      {
        clause: "Packaging or in-store use",
        why: "A face on a shelf talker or a pack is printed inventory, and the rights need to outlast the print run rather than the contract term.",
      },
      {
        clause: "Conduct provisions",
        why: "This is the clause that decides whether you can stop the association, and stop paying, if something happens away from the partnership.",
      },
      {
        clause: "Category exclusivity",
        why: "An ambassador free to appear for a competing brand next month removes most of the reason to build the association at all.",
      },
      {
        clause: "Direction, hours and equipment",
        why: "The more the brand directs the day-to-day, the more the arrangement looks like employment, and that determination varies by jurisdiction.",
      },
      {
        clause: "Content rights accumulating over the term",
        why: "A year of content licensed only for the term becomes unusable on the last day unless the grant is written to survive.",
      },
    ],
    cpgIssues: [
      "Ambassadors working in-store are inside a retailer's environment and are subject to that account's rules on demos, sampling, insurance and background checks, which the brand carries responsibility for.",
      "Athletes subject to competition testing frequently require third-party certification of a supplement or energy product before associating with it, and that certification has real cost and lead time.",
      "An ambassador's personal account of what a product does for them is a claim about the product in practice, so a written brief and an approval path matter throughout the term, not just at signing.",
      "Product allocation to ambassadors is inventory leaving the building — track it as shrink, set a monthly allowance and require reporting, or it quietly becomes a meaningful cost line.",
      "Category exclusivity in beverage is granular: energy, sports hydration, functional soda and protein can be one category or four, and the definition drives both price and conflict.",
      "Ambassadors who sample at events are handling food and drink in public, which brings the venue's own permitting and handling requirements into scope.",
    ],
    founderMistakes: [
      "Printing an ambassador's face on shelf talkers under a twelve-month agreement, then having in-market collateral featuring someone the brand had parted with.",
      "Running an ambassador programme with no conduct provision, and having no clean way to end an association that had become a problem.",
      "Directing ambassadors' hours and activities closely under a contractor agreement, without ever checking how that arrangement is treated where they work.",
      "Sending unlimited product to a roster of ambassadors with no allowance and no reporting, and losing track of several thousand units a year.",
      "Signing an athlete without confirming their existing sponsor commitments, which unwound the deal.",
    ],
    negotiationPoints: [
      "Ask for content rights that survive the term, and price any packaging or printed-collateral use separately with a sell-off window.",
      "Define category exclusivity by product type, listing what is included and excluded.",
      "Include a conduct provision with a defined process for suspending posting and payment.",
      "Set a monthly product allowance with reporting rather than an open supply line.",
      "Have counsel look at the working-status question before the programme scales beyond a handful of people.",
      "Require the ambassador to disclose the paid relationship in their own content as a condition of payment.",
    ],
    redFlags: [
      "Packaging or printed material use with no wind-down or sell-off period.",
      "No conduct provision on a multi-month deal.",
      "Content rights that expire with the term.",
      "Close direction of hours and duties on a contractor paper with no review of the arrangement.",
      "No representation that the individual is free of conflicting sponsorships.",
    ],
    beforeYouSign: [
      "Written confirmation of the individual's existing sponsorships and any governing-body or team obligations.",
      "A decision on whether packaging or printed collateral use is in scope, before anything is designed.",
      "Counsel's view on working status if the programme involves set hours, set locations or close direction.",
    ],
    related: [
      "brand-ambassador-agreement",
      "event-brand-ambassador-agreement",
      "influencer-agreement",
      "nil-agreement",
      "independent-contractor-agreement",
      "influencer-affiliate-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Long-running individual relationships raise worker-classification questions that vary by jurisdiction, and packaging or printed-collateral use makes an ending expensive to unwind.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sponsorship-agreement",
    name: "Sponsorship Agreement",
    category: "marketing",
    subcategory: "Sponsorship and field marketing",
    aliases: [
      "Brand Sponsorship Contract",
      "Team Sponsorship Agreement",
      "Partnership Sponsorship Agreement",
      "Property Sponsorship Deal",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Property (team, venue, festival, series, podcast or organisation)",
    whenYouNeedIt:
      "When you are paying a property — a team, a race series, a festival, a gym chain, a podcast — for association, visibility and usually the right to sample or sell on site.",
    whatItDoes:
      "Buys a defined package of rights: logo placement, category exclusivity, on-site sampling or sales, content capture, hospitality and the right to describe yourself as a sponsor, in exchange for fee and often product.",
    whyItMatters:
      "Sponsorship is sold as a deck of impressions and delivered as a list of obligations, and the gap between the two is where the money goes. The rights that actually matter to a CPG brand are the operational ones: can you sample on site, can you sell, can you get product to the venue cold, is the category exclusivity written so a competitor cannot appear as a supplier rather than a sponsor, and do you own the footage and photography of your own activation. Founders pay for a logo on a banner and discover the venue already has a beverage pouring contract that prevents them from handing out a single can.",
    majorTerms: [
      "Rights package: signage, digital, programme, announcements, hospitality",
      "Category exclusivity and how the category is defined",
      "On-site sampling rights and the volume permitted",
      "On-site sales rights and revenue share, if any",
      "Product supply obligations and who pays freight",
      "Content capture rights and ownership of resulting assets",
      "Use of the property's marks and approval process",
      "Activation space, power, storage and refrigeration",
      "Measurement, reporting and proof of delivery",
      "Term, renewal rights and right of first refusal",
      "Cancellation, postponement and refund mechanics",
    ],
    clausesToWatch: [
      {
        clause: "Category definition for exclusivity",
        why: "A category written as 'energy drink' leaves a functional soda or a hydration brand free to appear, and the definition is where the value of exclusivity actually sits.",
      },
      {
        clause: "Existing venue and supplier agreements",
        why: "Many venues have pre-existing pouring or supply arrangements that override what a sponsorship seller can grant you on site.",
      },
      {
        clause: "Sampling rights and permissions",
        why: "The right to sample in the contract is not the same as permission to sample from the venue and whoever permits food and drink service there.",
      },
      {
        clause: "Content ownership",
        why: "Photography and footage captured at the property is often owned by the property, so the images of your own activation may come with a licence rather than to you.",
      },
      {
        clause: "Cancellation and postponement",
        why: "Fees paid, product shipped and collateral printed do not automatically come back when an event moves or is cancelled.",
      },
      {
        clause: "Proof of delivery",
        why: "Without a delivery schedule and evidence obligation, a sponsorship can under-deliver quietly and you have nothing to point at.",
      },
    ],
    cpgIssues: [
      "Venues and stadiums frequently hold exclusive pouring or supply contracts, so a sponsorship that sounds like it includes on-site presence may not permit a single unit to be handed out.",
      "Sampling a beverage or a food product on site brings the venue's own permitting, handling and staffing requirements into play, and those sit with you rather than with the property.",
      "Cold-chain product needs refrigerated storage on site, which is a negotiated line item and is often simply unavailable — ambient sampling of a chilled product does not represent it well.",
      "Product supplied as part of a sponsorship is inventory at full landed cost, so a package quoted as 'fee plus product' is more expensive than it appears once cases, freight and pallets are counted.",
      "Category exclusivity in beverage and snack is granular and every property defines it loosely by default; energy, sports drink, functional water, protein and better-for-you soda are distinct in practice.",
      "Leftover branded cups, coolers and collateral produced for one event carry print minimums and become dead stock if the property changes its rules or the deal is not renewed.",
    ],
    founderMistakes: [
      "Paying for a season of visibility at a venue that already had a beverage supply contract, and never being allowed to sample.",
      "Accepting a category definition broad enough to sound exclusive and narrow enough to let a direct competitor in as an official supplier.",
      "Shipping pallets of chilled product to a site with no refrigeration and sampling a warm version of a product built to be cold.",
      "Counting product supplied at wholesale value rather than landed cost, and understating the real price of the sponsorship by a wide margin.",
      "Discovering the property owned all photography from the activation and licensing back images of their own booth.",
    ],
    negotiationPoints: [
      "Write the exclusive category yourself, listing included and excluded product types, and require notice of any supplier arrangement that touches it.",
      "Confirm in writing that the property has authority to grant on-site sampling and sales at each venue.",
      "Value the product component at landed cost and negotiate the fee against the true total.",
      "Ask for ownership, or a perpetual all-media licence, of content capturing your own activation.",
      "Include cancellation and postponement mechanics covering fee, shipped product and printed collateral.",
      "Attach a delivery schedule with dates and evidence requirements, and tie a portion of the fee to it.",
    ],
    redFlags: [
      "Category exclusivity described in a single generic word.",
      "No confirmation that the property can actually grant on-site sampling or sales rights.",
      "No cancellation or postponement terms, or a fee that is non-refundable in all circumstances.",
      "Content from your activation owned entirely by the property.",
      "A product supply obligation with no cap and no defined delivery points.",
    ],
    beforeYouSign: [
      "Written confirmation of any existing pouring, supply or category arrangements at every venue in scope.",
      "The full landed cost of the product component, calculated before the fee is agreed.",
      "The activation logistics — space, power, storage, refrigeration, staffing — confirmed in writing.",
    ],
    related: [
      "event-sponsorship-agreement",
      "event-sponsorship-contract",
      "promotional-partnership-agreement",
      "sampling-agreement",
      "event-participation-agreement",
      "ambassador-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "event-sponsorship-agreement",
    name: "Event Sponsorship Agreement",
    category: "marketing",
    subcategory: "Sponsorship and field marketing",
    aliases: [
      "Festival Sponsorship Agreement",
      "Conference Sponsorship Contract",
      "Race Sponsorship Agreement",
      "Trade Show Sponsorship",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Event Organiser",
    whenYouNeedIt:
      "When sponsoring a single, dated event — a festival, a race, an industry show, a conference — usually months ahead of the date, with product and printed material committed well before that.",
    whatItDoes:
      "Buys a defined package at one event: branding, activation space, sampling rights, attendee access and content, with the fee typically paid up front and the deliverables landing on a single day.",
    whyItMatters:
      "Everything in an event sponsorship converges on one date, which makes cancellation and postponement the terms that matter most and the ones founders read last. By the time a date moves you have already paid the fee, printed the booth graphics, produced branded cups and shipped pallets of product to a receiving dock that will not store them. A rescheduled event with a non-refundable fee and no product-storage obligation converts a marketing spend into a write-off. The rest of the agreement is a list of promises about attendance and visibility that is worth exactly as much as the delivery schedule attached to it.",
    majorTerms: [
      "Package inclusions listed item by item",
      "Activation space, location on the floor plan and size",
      "Sampling rights, permitted volumes and serving format",
      "On-site sales rights and revenue share",
      "Attendance estimates and what happens if they are missed",
      "Cancellation, postponement and force majeure",
      "Freight, receiving windows, storage and refrigeration",
      "Load-in, load-out and drayage costs",
      "Insurance and indemnity requirements",
      "Use of the event's marks and attendee lists",
      "Content capture and photography rights",
    ],
    clausesToWatch: [
      {
        clause: "Cancellation and postponement",
        why: "A non-refundable fee plus shipped product and printed collateral turns a moved date into a total loss unless the agreement says otherwise.",
      },
      {
        clause: "Attendance representations",
        why: "Fees are priced off projected attendance, and without a remedy the projection carries no weight when the event is half the size promised.",
      },
      {
        clause: "Sampling permissions and volume",
        why: "The organiser's grant is not the venue's permission, and sampling volume limits can make an activation pointless if discovered on the day.",
      },
      {
        clause: "Freight, receiving and drayage",
        why: "These costs are often not in the sponsorship fee at all, and at large venues they can rival the fee itself.",
      },
      {
        clause: "Storage and refrigeration",
        why: "A chilled product with nowhere cold to sit is served warm or not at all, and the obligation to provide storage needs to be explicit.",
      },
      {
        clause: "Attendee data",
        why: "Lists and scanned leads come with their own consent conditions, and what you may do with them afterwards is usually narrower than expected.",
      },
    ],
    cpgIssues: [
      "Sampling food and drink at a venue triggers that venue's permitting and handling requirements, and organisers frequently pass those obligations to sponsors without saying so clearly.",
      "Refrigerated and frozen product needs cold storage from the receiving dock to the booth, and a missing link anywhere in that chain means product that cannot be served.",
      "Event freight has narrow receiving windows and steep late fees, and a production run that slips by a week can miss the window entirely.",
      "Product shipped to an event is inventory that rarely comes back — plan for it as a full write-off at landed cost rather than as a marketing line at wholesale.",
      "Branded cups, sample sizes and single-serve formats are separate production runs with their own minimums and lead times, and they are only useful at events like this one.",
      "Trade shows put your product beside competitors and in front of buyers, so sell sheets and pricing at the booth need to match what your broker and distributor are quoting.",
    ],
    founderMistakes: [
      "Paying a non-refundable fee for an event that moved by six months, with printed collateral carrying the original date.",
      "Shipping pallets to a venue with no cold storage and serving a chilled functional beverage at ambient temperature all weekend.",
      "Budgeting the sponsorship fee and discovering drayage, electrical and booth-build costs afterwards.",
      "Producing a branded sample cup at minimum order quantity for one event and writing off most of the run.",
      "Missing the receiving window by two days and paying to have product held off-site.",
    ],
    negotiationPoints: [
      "Negotiate postponement terms that roll the fee to the new date and address shipped product and printed collateral.",
      "Ask for the full cost picture in writing: fee, drayage, electrical, booth build, storage and staffing.",
      "Confirm sampling permissions, volume limits and serving format before committing to sample production.",
      "Require confirmation of cold storage and a receiving window that works against your production calendar.",
      "Ask for a remedy — credit or partial refund — if attendance falls materially short of what was represented.",
      "Secure ownership or a perpetual licence to photography and footage of your own activation.",
    ],
    redFlags: [
      "A non-refundable fee with no postponement mechanics of any kind.",
      "Sampling described as included with no reference to volume, permissions or serving format.",
      "No mention of freight, receiving windows, storage or drayage.",
      "Attendance figures with no basis and no remedy.",
      "Broad rights for the organiser to use your marks with no approval.",
    ],
    beforeYouSign: [
      "The full landed cost of everything you will ship, printed and produced for the event.",
      "Written confirmation of sampling permissions, storage and the receiving window.",
      "The cancellation and postponement terms, read before the fee is paid.",
    ],
    related: [
      "sponsorship-agreement",
      "event-sponsorship-contract",
      "event-participation-agreement",
      "event-sampling-agreement",
      "sampling-agency-agreement",
      "promotional-services-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sampling-agency-agreement",
    name: "Sampling Agency Agreement",
    category: "marketing",
    subcategory: "Sponsorship and field marketing",
    aliases: [
      "Field Marketing Agency Agreement",
      "Demo Agency Agreement",
      "Experiential Marketing Agreement",
      "Brand Activation Agency Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Field Marketing or Sampling Agency",
    whenYouNeedIt:
      "When an outside agency will staff in-store demos, street sampling or event activations on your behalf, usually across more locations than you could cover yourself.",
    whatItDoes:
      "Engages an agency to plan and staff sampling and demo activity: who works the shifts, how product gets to them, what they are told to say, what gets reported back and who is accountable for the inventory.",
    whyItMatters:
      "Sampling programmes fail in two places, and neither is the sampling. The first is inventory: product sent to a distributed roster of demo staff is stock leaving your control, and without per-shift allocation, reconciliation and photo proof, a meaningful share of it simply disappears into a cost line nobody can explain. The second is what gets said. A demonstrator standing next to your product in a store is speaking for the brand, and the brand carries the exposure for what they tell a shopper about a supplement or a functional drink. A written script and a do-not-say list are not bureaucracy; they are the only control you have over a hundred conversations you will never hear.",
    majorTerms: [
      "Locations, shift count and hours per shift",
      "Fee structure: per shift, per hour or per location, plus management fee",
      "Product allocation per shift and reconciliation requirements",
      "Freight, storage and who receives product locally",
      "Script, talking points and a do-not-say list",
      "Reporting: photos, sample counts, scan data and shopper feedback",
      "Staff training, certification and background requirements",
      "Retailer-specific rules, insurance and vendor compliance",
      "Ownership of photography and content captured on shift",
      "Escalation for consumer complaints or product issues",
      "Term, minimum commitments and cancellation windows",
    ],
    clausesToWatch: [
      {
        clause: "Product reconciliation",
        why: "Without a per-shift count in, count out and photo evidence, sampling inventory shrinkage is invisible until you reconcile the year.",
      },
      {
        clause: "Script and claim control",
        why: "The demonstrator is speaking for the brand, and an agency that treats the script as guidance rather than as a requirement is a live exposure.",
      },
      {
        clause: "Retailer compliance obligations",
        why: "Accounts impose their own demo rules — approved vendors, insurance certificates, food handling, background checks — and non-compliance is your problem with the account, not the agency's.",
      },
      {
        clause: "Ownership of content captured on shift",
        why: "Photos and video from demos are usable marketing assets, and they need shopper releases and a clear owner.",
      },
      {
        clause: "Minimum commitments and cancellation",
        why: "Programmes are sold on volume tiers, and a minimum shift commitment locks spend in ahead of any evidence that the sampling works.",
      },
      {
        clause: "Staffing substitution",
        why: "A named lead demonstrator who is replaced by whoever is available is the difference between a trained brand advocate and a stranger at a table.",
      },
    ],
    cpgIssues: [
      "Product for demos ships to homes, lockers and store back rooms with no temperature control, which rules out refrigerated and short-code items unless the logistics are solved first.",
      "Retail accounts frequently require demo staff to be approved vendors, to carry certificates of insurance naming the retailer, and to hold food-handling certification — the requirements come from the account and change by banner.",
      "Sampling volume is real inventory at landed cost, and a programme quoted per shift plus product is far more expensive than the shift rate alone suggests.",
      "Demonstrators are asked constantly whether a functional product helps with a specific condition, and the answer has to be scripted rather than improvised.",
      "Scan-lift measurement needs the store, date and shift recorded accurately, or you cannot tell whether the programme did anything and cannot justify it to a buyer.",
      "Open sampling of food and beverage brings the venue's or retailer's handling requirements into play, including serving format, utensils, gloves and how long product may sit out.",
      "Free fills and demo product pulled from store stock come back as deductions rather than as marketing spend, so agree in advance where the sampling product comes from.",
    ],
    founderMistakes: [
      "Sending cases directly to demo staff with no reconciliation and losing a large share of a programme's inventory with no way to account for it.",
      "Running demos in an account without the retailer's required insurance certificate and being removed from the store's demo calendar.",
      "Briefing an agency verbally and finding demonstrators telling shoppers the product helped with specific health conditions.",
      "Committing to a minimum shift block before testing whether sampling moved velocity in a single store.",
      "Shipping a refrigerated product to a demo team with no cold storage and sampling it warm.",
      "Failing to record store and date per shift, and being unable to show a buyer any lift from the programme.",
    ],
    negotiationPoints: [
      "Require per-shift product reconciliation with counts and photo evidence as a condition of invoice approval.",
      "Supply a written script, required talking points and a do-not-say list, and make adherence a contractual obligation.",
      "Push retailer compliance — approvals, insurance, certifications — onto the agency, with evidence delivered before the first shift.",
      "Start with a pilot block of shifts and measurable stores rather than an annual minimum.",
      "Ask for ownership of photography from shifts, with a shopper release process built into the brief.",
      "Agree where demo product comes from and how it is accounted for, so it does not arrive as a retailer deduction.",
    ],
    redFlags: [
      "No product reconciliation process of any kind.",
      "Scripts described as suggestions rather than requirements.",
      "The agency unwilling to take responsibility for retailer compliance or insurance.",
      "A large minimum commitment before any pilot.",
      "Reporting limited to shift completion with no store-level data.",
    ],
    beforeYouSign: [
      "A landed-cost model of the programme including product, freight and shrinkage, not just shift fees.",
      "The written script and do-not-say list, reviewed before any staffing begins.",
      "The demo requirements from each retail account you intend to run in.",
    ],
    related: [
      "sampling-agreement",
      "demo-agreement",
      "demo-contractor-agreement",
      "event-sampling-agreement",
      "promotional-services-agreement",
      "event-brand-ambassador-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promotional-services-agreement",
    name: "Promotional Services Agreement",
    category: "marketing",
    subcategory: "Sponsorship and field marketing",
    aliases: [
      "PR Agency Agreement",
      "Publicity Services Agreement",
      "Promotions Agency Agreement",
      "Brand Promotion Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Promotional or PR Agency",
    whenYouNeedIt:
      "When an agency will run promotional activity on the brand's behalf: press outreach, gifting and seeding programmes, contests and giveaways, product placement, awards submissions and launch stunts.",
    whatItDoes:
      "Covers promotional and publicity services: what the agency will do, what they will produce, what they may commit to in your name, and who owns the assets, relationships and coverage that result.",
    whyItMatters:
      "Two things routinely go sideways. The first is authority: an agency pitching, gifting and entering you into things is making commitments in your name, and without a written approval threshold you find out about an obligation when it lands. The second is that promotions with a prize element — giveaways, contests, sweepstakes — carry their own rules requirements that vary by jurisdiction and are easy to trip over when the prize is a consumable being shipped somewhere you do not normally ship. Agencies launch these on a content calendar. They should not launch without written rules that counsel has seen.",
    majorTerms: [
      "Scope: press, gifting, contests, placement, awards, events",
      "Approval thresholds and what the agency may commit to unilaterally",
      "Fees, pass-through costs and markups",
      "Product for gifting and seeding, and who pays freight",
      "Ownership of media lists, contacts and campaign assets",
      "Rights in photography and content produced for promotions",
      "Responsibility for contest and giveaway rules",
      "Reporting: coverage, reach and what counts as a result",
      "Confidentiality of launch timing and unreleased product",
      "Term, notice and handover of active relationships",
    ],
    clausesToWatch: [
      {
        clause: "Authority to commit",
        why: "An agency accepting an award entry fee, a placement cost or a gifting obligation in your name creates a cost you did not approve.",
      },
      {
        clause: "Ownership of media lists and contacts",
        why: "The relationships built during the engagement are often treated as agency property, so you leave with coverage and no contacts.",
      },
      {
        clause: "Responsibility for promotion rules",
        why: "Contests and giveaways have rules requirements that differ by jurisdiction, and an agency drafting them from a template is not a substitute for review.",
      },
      {
        clause: "Pass-through costs and markups",
        why: "Gifting product, freight, mailers, event costs and placement fees run through the agency, and undisclosed markups sit inside all of them.",
      },
      {
        clause: "Embargo and confidentiality",
        why: "Launch timing, new SKUs and retailer wins are commercially sensitive, and an early pitch can break an account's own announcement plan.",
      },
      {
        clause: "Definition of a result",
        why: "Without an agreed definition, coverage in outlets with no relevance to your buyer counts the same as the placement that actually mattered.",
      },
    ],
    cpgIssues: [
      "Gifted product shipped to press and creators is inventory with shelf life, no cold chain and unpredictable delivery timing — short-code and refrigerated items frequently arrive unusable.",
      "Prizes that are consumable products create shipping problems in jurisdictions where you do not normally sell, and the rules for running a promotion there may differ from where you are based.",
      "Press samples of unreleased flavours disclose product before launch, which can breach an embargo you agreed with a retail account.",
      "Coverage often reproduces claims from your materials, and an inaccurate claim in a press release travels further and is harder to correct than one on your own site.",
      "Mailers and press kits are production runs with minimums and lead times, and they age out the moment packaging changes.",
      "Awards and certification submissions frequently require samples, fees and formulation disclosure, and some carry ongoing usage rules for the badge on pack.",
    ],
    founderMistakes: [
      "Letting an agency run a giveaway with no written rules, then unwinding it after entries came from places the product could not be shipped to.",
      "Discovering the agency owned the media list built during a year of work, and leaving with no contacts.",
      "Approving a press kit at minimum order quantity weeks before a packaging change made every kit obsolete.",
      "Having an agency pitch a retailer launch ahead of the account's own announcement.",
      "Paying pass-through gifting costs with an undisclosed markup on freight and product handling.",
    ],
    negotiationPoints: [
      "Set a written approval threshold above which the agency may not commit the brand to any cost.",
      "Require that media lists, contacts and campaign assets built during the engagement are delivered to you on exit.",
      "Keep responsibility for contest and giveaway rules with counsel, and make the agency's launch conditional on written rules being in place.",
      "Require disclosure of all markups and the right to see third-party invoices.",
      "Agree in writing what counts as a result before the first month's reporting.",
      "Bind the agency to launch embargoes and to your retail partners' announcement timing.",
    ],
    redFlags: [
      "Contests or giveaways launched without written rules.",
      "Media lists and contacts described as agency property.",
      "No approval threshold for commitments made in your name.",
      "Reporting measured purely on volume of coverage.",
      "No confidentiality provision covering unreleased product and launch timing.",
    ],
    beforeYouSign: [
      "A written approval threshold and the escalation path for anything above it.",
      "Confirmation of who drafts and reviews rules for any promotion with a prize element.",
    ],
    related: [
      "promotional-agreement",
      "contest-rules",
      "sweepstakes-rules",
      "product-giveaway-rules",
      "sampling-agency-agreement",
      "marketing-agency-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default MARKETING_DOCS;
